import { useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { IonContent, IonPage, IonSpinner } from '@ionic/react';
import { useResolveShareLink } from '../hooks/useListings';
import { SHARE_ARRIVAL_AT_KEY } from '../services/authStorageKeys';

const SHARE_REFERRAL_KEY = 'hp_share_referral';

export interface ShareReferral {
  ref: string;
  listingId?: string;
  sharedBy: { id: string; displayName: string; avatarUrl: string };
  timestamp: number;
}

export function getShareReferral(): ShareReferral | null {
  try {
    const raw = localStorage.getItem(SHARE_REFERRAL_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export const ShareRedirectPage: React.FC = () => {
  const location = useLocation();
  const history = useHistory();
  const ref = new URLSearchParams(location.search).get('ref');
  const { data, error } = useResolveShareLink(ref);

  // Stamp the share-arrival flag the moment this page mounts (NOT when share
  // resolves) so the auth chain's `logout()` can read it during the in-flight
  // race. The 30s grace covers the bootstrap window; later legitimate
  // logouts are unaffected.
  useEffect(() => {
    try {
      sessionStorage.setItem(SHARE_ARRIVAL_AT_KEY, String(Date.now()));
    } catch {
      // sessionStorage unavailable (private mode quota, etc.) — degrade
      // silently. Auth chain falls back to default hard-logout behavior.
    }
  }, []);

  useEffect(() => {
    if (data) {
      const referral: ShareReferral = {
        ref: ref!,
        listingId: data.listingId,
        sharedBy: data.sharedBy,
        timestamp: Date.now(),
      };
      localStorage.setItem(SHARE_REFERRAL_KEY, JSON.stringify(referral));

      // Scan-to-connect handshake: when an already-registered caller resolves an
      // app-share link, the backend mints a pending connection request from the
      // sharer and returns its id. Route the scanner to the `/c={id}` accept/
      // decline card (interstitial) instead of straight to the feed. Accept,
      // decline and maybe-later there each navigate onward. The referral is
      // already stored above, so app-share attribution is preserved either way.
      //
      // Listing shares never carry this id: the backend still creates the
      // pending request but withholds it here, so the shared dish gets
      // delivered and the request waits in the recipient's connections list.
      if (data.pendingConnectionId) {
        history.replace(`/c=${data.pendingConnectionId}`);
        return;
      }

      const utm = `utm_source=share&utm_medium=link&utm_content=${data.sharedBy.id}`;
      const target = data.listingId ? `/listing/${data.listingId}?${utm}` : `/?${utm}`;
      // SPA route change — the share-arrival flag stamped in the mount effect
      // above prevents the parallel auth chain (whoami → refresh-fail →
      // `logout()`) from doing its `window.location.assign('/')` and
      // hijacking the redirect.
      history.replace(target);
    }
  }, [data, ref, history]);

  useEffect(() => {
    if (error) {
      history.replace('/');
    }
  }, [error, history]);

  if (!ref) {
    history.replace('/');
    return null;
  }

  return (
    <IonPage>
      <IonContent>
        <div className="flex h-full items-center justify-center">
          <IonSpinner name="crescent" />
        </div>
      </IonContent>
    </IonPage>
  );
};
